import type { ReactNode } from "react";
import { physicalKeysFor, splitNotations } from "@/presentation/i18n/jis-keyboard";
import "./KeyCap.css";

export type KeyCapState = "learned" | "pending" | "unlearned" | "modifier";

interface KeyCapProps {
  readonly children: ReactNode;
  /** 既定は`"learned"`（既習・通常）。 */
  readonly state?: KeyCapState;
  /** Shift・Ctrl のような修飾キー（幅広・薄い面で描く）。 */
  readonly modifier?: boolean;
  /** Enter・Esc のような名前付きの幅広キー。 */
  readonly wide?: boolean;
}

/**
 * 物理キー1つぶんの札（キーキャップ）。実物のキーに寄せて、天面・側面・沈み込みを持つ
 * 立体の形で描く（ui-design.md §5.2 の3状態に「修飾キー」を足した4状態）。
 */
export function KeyCap({
  children,
  state = "learned",
  modifier = false,
  wide = false,
}: KeyCapProps) {
  // 「Enter」「Tab」のように刻印が長いものは、指定がなくても幅広の札として描く。
  const isWide = wide || (typeof children === "string" && children.length > 1 && !modifier);
  const classes = [
    "keycap",
    state !== "learned" ? `keycap--${state}` : null,
    modifier ? "keycap--mod" : null,
    isWide ? "keycap--wide" : null,
  ]
    .filter(Boolean)
    .join(" ");
  return <span className={classes}>{children}</span>;
}

interface KeyChordProps {
  /** vimの打鍵表記（`w` / `dw` / `<C-r>` / `$` / `Shift`）。 */
  readonly notation: string;
  readonly state?: KeyCapState;
}

/**
 * 打鍵表記を、日本語（JIS）配列で**実際に押す物理キーの並び**として描く。
 * `$` は Shift + 4、`<C-r>` は Ctrl + R、`dw` は D と W の2打——というように、
 * 画面の記号と手の動きが1対1で結びつくようにする（`jis-keyboard.ts`）。
 */
export function KeyChord({ notation, state = "learned" }: KeyChordProps) {
  const keys = splitNotations(notation).flatMap((single) => physicalKeysFor(single));
  return (
    <span className="keychord">
      {keys.map((key, index) => (
        <KeyCap
          // biome-ignore lint/suspicious/noArrayIndexKey: 同じ刻印が並ぶ（gg → G G）ため、位置そのものが識別子になる
          key={`${key.legend}-${index}`}
          state={state}
          modifier={key.modifier ?? false}
          wide={key.wide ?? false}
        >
          {key.legend}
        </KeyCap>
      ))}
    </span>
  );
}
